import { createContext, useContext, useMemo } from 'react'

import { useAppFoundationState } from './context/useAppFoundationState.js'
import { useAppNavigationState } from './context/useAppNavigationState.js'
import { useAppSectionRegistry } from './context/useAppSectionRegistry.js'

import { useAppCapabilities }       from './hooks/useAppCapabilities.js'
import { useHeroCapabilities } from '../domains/hero/hooks/useHeroCapabilities.js'
import { normalizeAppUrlState, parseAppUrlState } from '../domains/navigation/utils/appRouteState.js'
import { resolveEffectiveSettings } from './utils/resolveEffectiveSettings.js'
import { resolveEffectiveStyles }   from './utils/resolveEffectiveStyles.js'

import { SECTION_ID } from '../shared/constants/sectionIds.js'

export const AppContext = createContext(null)

/**
 * Global application context provider.
 * Aggregates cross-domain state: config, device layout, nav, favorites.
 * Domain-specific contexts (CatalogContext, ProductContext, etc.)
 * are mounted inside their respective domain components — not here.
 */
export function AppContextProvider({ children }) {
  const foundationState = useAppFoundationState()
  const sectionRegistry = useAppSectionRegistry()
  const heroCapabilities = useHeroCapabilities(foundationState.appConfig.projectConfig, foundationState.appConfig.panelSettings);
  
  const resolvedEffectiveSettings = useMemo (() => resolveEffectiveSettings({
    projectConfig: foundationState.appConfig.projectConfig,
    panelSettings: foundationState.appConfig.panelSettings,
  }), [foundationState])
  const resolvedEffectiveStyles = useMemo(() => resolveEffectiveStyles({
    projectConfig: foundationState.appConfig.projectConfig,
    panelStyles: foundationState.appConfig.panelStyles,
  }), [foundationState])
  const appCapabilities = useAppCapabilities({
    projectConfig: foundationState.appConfig.projectConfig,
    panelSettings: resolvedEffectiveSettings,
    panelStyles: resolvedEffectiveStyles,
  })

  // Resolve initialHeroView accounting for URL override.
  // When config is available and the URL carries an explicit `bim-view` param,
  // that value should win over the settings default — preventing the
  // NULL → config-default → URL-override triple-flip on first paint.
  const resolvedInitialHeroView = useMemo(() => {
    if (!heroCapabilities.hasHeroSection) return heroCapabilities.initialView
    const parsed = parseAppUrlState(window.location)
    if (!parsed.sectionView) return heroCapabilities.initialView
    const { sectionView } = normalizeAppUrlState(
      {
        sectionId: SECTION_ID.HERO,
        sectionView: parsed.sectionView,
        productRef: null,
        buildingId: null,
        tierRef: null,
        source: { usedLegacyProductParam: false },
      },
      { defaultHeroView: heroCapabilities.initialView, allowedHeroViews: heroCapabilities.availableViews },
    )
    return sectionView ?? heroCapabilities.initialView
  }, [heroCapabilities])

  const navigationState = useAppNavigationState({
    scrollToSectionWhenReady: sectionRegistry.scrollToSectionWhenReady,
    initialHeroView: resolvedInitialHeroView,
  })

  const value = {
    // Container ref — attached to app root div in App.jsx
    containerRef: foundationState.containerRef,
    appProjectId: foundationState.appProjectId,
    appRootId: foundationState.appRootId,
    portalHostId: foundationState.portalHostId,
    // Responsive layout state derived from container width (not viewport)
    ...foundationState.deviceLayout,
    // Project configuration: hard config from panel-config.json + soft settings
    appCapabilities,
    heroCapabilities,
    projectConfig: foundationState.appConfig.projectConfig,
    // panelSettings: foundationState.appConfig.panelSettings,
    panelSettings: resolvedEffectiveSettings,
    panelStyles: resolvedEffectiveStyles,
    windowData: foundationState.appConfig.windowData,
    configStatus: foundationState.appConfig.configStatus,
    configError: foundationState.appConfig.configError,
    // Logo resolved once (project → developer → APLUS mark fallback)
    logoSrc: foundationState.logoSrc,
    // Favorites — cross-domain, backed by localStorage keyed by projectId
    isFavorite: foundationState.favorites.isFavorite,
    toggleFavorite: foundationState.favorites.toggleFavorite,
    clearFavorites: foundationState.favorites.clearFavorites,
    favoritesCount: foundationState.favorites.favoritesCount,
    favoriteUuids: foundationState.favorites.favoriteUuids,
    // Navigation state — active product/building/tier (cross-domain)
    // Identifiers: uuid for products, name_original for buildings, ref_tier (number) for tiers.
    activeProduct: navigationState.activeProduct,
    activeBuilding: navigationState.activeBuilding,
    activeTier: navigationState.activeTier,
    // activeSectionId — App-level section state used by URL sync, desktop depth gating,
    // and named section navigation / scroll handoffs.
    activeSectionId: navigationState.activeSectionId,
    // activeHeroView — App-level routable sub-view state for the hero section.
    activeHeroView: navigationState.activeHeroView,
    // Section refs — attach to domain section root divs; consumed by scrollToSection
    sectionRefs: sectionRegistry.sectionRefs,
    scrollToSection: sectionRegistry.scrollToSection,
    scrollToSectionWhenReady: sectionRegistry.scrollToSectionWhenReady,
    // Preferred cross-domain navigation API.
    navigationActions: navigationState.navigationActions,
    activateSectionHandler: navigationState.activateSectionHandler,
    replaceNavigationStateHandler: navigationState.replaceNavigationStateHandler,
    clearProductSelectionHandler: navigationState.clearProductSelectionHandler,
    selectProductHandler: navigationState.selectProductHandler,
    changeHeroViewHandler: navigationState.changeHeroViewHandler,
    // Compatibility aliases kept until downstream call sites migrate to named actions.
    setActiveProduct: navigationState.setActiveProduct,
    setActiveBuilding: navigationState.setActiveBuilding,
    setActiveTier: navigationState.setActiveTier,
    setActiveSectionId: navigationState.setActiveSectionId,
    setActiveHeroView: navigationState.setActiveHeroView,

    // Further state added in subsequent steps: lang, mobileNav
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

/**
 * Hook to consume the global AppContext.
 * @returns {object} AppContext value
 */
export function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used within AppContextProvider')
  return ctx
}
